import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './ChefDashboard.css';
import workTicketService from '../../../services/api/workTicketService';

const ChefDashboard = () => {
  const { workerId } = useParams();
  const [tickets, setTickets] = useState([]);
  const [chefName, setChefName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [completingTickets, setCompletingTickets] = useState(new Set());

  const isWorkerView = Boolean(workerId);

  const fetchTickets = async () => {
    try {
      setError(null);
      let response;

      if (isWorkerView) {
        response = await workTicketService.getWorkerTickets(workerId);
      } else {
        response = await workTicketService.getPendingCookTickets();
      }
      
      if (response.statusCode === 200 && response.data) {
        let ticketData = response.data;

        if (isWorkerView) {
          // For worker view, only show OPEN tickets
          ticketData = ticketData.filter(t => t.status === 'OPEN');
          // Derive chef name from the first ticket that has it
          if (!chefName && ticketData.length > 0) {
            setChefName(ticketData[0].workerName || workerId);
          } else if (!chefName) {
            setChefName(workerId);
          }
        }

        setTickets(ticketData);
      } else {
        setTickets([]);
      }
    } catch (err) {
      setError('Failed to load tickets. Please try again.');
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [workerId]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchTickets();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleCompleteTicket = async (ticketId) => {
    setCompletingTickets(prev => new Set(prev).add(ticketId));
    
    try {
      const response = await workTicketService.completeTicket(ticketId);
      
      if (response.statusCode === 200) {
        setTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
      } else {
        setError('Failed to complete ticket. Please try again.');
      }
    } catch (err) {
      setError('Failed to complete ticket. Please try again.');
      console.error('Error completing ticket:', err);
    } finally {
      setCompletingTickets(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  if (loading) {
    return (
      <div className="chef-dashboard">
        <div className="loading-spinner">Loading tickets...</div>
      </div>
    );
  }

  return (
    <div className="chef-dashboard">
      <div className="chef-header">
        <h1>👨‍🍳 {isWorkerView ? `${chefName || workerId}'s Dashboard` : 'Chef Dashboard'}</h1>
        <p className="subtitle">
          {isWorkerView ? `Viewing orders for ${chefName || workerId}` : 'Manage your cooking orders'}
        </p>
      </div>

      <div className="chef-controls">
        <button 
          className={`chef-control-btn ${autoRefresh ? 'active' : ''}`}
          onClick={() => setAutoRefresh(!autoRefresh)}
        >
          <span>{autoRefresh ? '🔄 Live' : '⏸️ Paused'}</span>
        </button>
        <button 
          className="chef-control-btn"
          onClick={fetchTickets}
          disabled={loading}
        >
          <span>↻ Refresh</span>
        </button>
      </div>

      <div className="chef-tickets-container">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{tickets.length}</span>
            <span className="stat-label">Pending Orders</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {tickets.filter(t => 
                new Date() - new Date(t.createdAt) > 600000
              ).length}
            </span>
            <span className="stat-label">Over 10 mins</span>
          </div>
        </div>

        {tickets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✅</div>
            <h3>All caught up!</h3>
            <p>No pending orders at the moment.</p>
          </div>
        ) : (
          <div className="tickets-grid">
            {tickets.map(ticket => {
              const isCompleting = completingTickets.has(ticket.id);
              
              return (
                <div key={ticket.id} className="ticket-card">
                  <div className="ticket-header">
                    <span className="ticket-id">#{ticket.id}</span>
                    <span className="ticket-time">{getTimeAgo(ticket.createdAt)}</span>
                  </div>

                  <div className="ticket-body">
                    <div className="ticket-info-row">
                      <span className="ticket-label">Order ID:</span>
                      <span className="ticket-value">{ticket.orderId}</span>
                    </div>
                    <div className="ticket-info-row">
                      <span className="ticket-label">Item:</span>
                      <span className="ticket-value">{ticket.itemName}</span>
                    </div>
                    <div className="ticket-info-row">
                      <span className="ticket-label">Quantity:</span>
                      <span className="ticket-value">{ticket.quantity}</span>
                    </div>
                    {!isWorkerView && (
                      <div className="ticket-info-row">
                        <span className="ticket-label">Assigned to:</span>
                        <span className="ticket-value">{ticket.workerName}</span>
                      </div>
                    )}
                    <div className="ticket-info-row">
                      <span className="ticket-label">Time:</span>
                      <span className="ticket-value">{formatTime(ticket.createdAt)}</span>
                    </div>

                    {ticket.instructions && (
                      <div className="ticket-instructions">
                        <span className="ticket-instructions-label">⚠️ Special Instructions:</span>
                        <p className="ticket-instructions-text">{ticket.instructions}</p>
                      </div>
                    )}
                  </div>

                  <div className="ticket-footer">
                    <button 
                      className={`complete-btn ${isCompleting ? 'completing' : ''}`}
                      onClick={() => handleCompleteTicket(ticket.id)}
                      disabled={isCompleting}
                    >
                      {isCompleting ? '⏳ Completing...' : '✅ Mark as Complete'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChefDashboard;

